import * as React from "react"
import { Map, Marker, Popup, LngLatLike } from "mapbox-gl"
import ReactDOM from "react-dom"
import { defineMessages, FormattedMessage, useIntl } from "react-intl"
import { ILocation } from "../../interfaces/location"
import { IWhere2GoData } from "../../interfaces/where2go"
import Loading from "../Loading"
import BestWeatherControls from "./BestWeatherControls"
import { useDispatch, useSelector } from "react-redux"
import { IWhere2goStore } from "../../redux/reducers/where2go"
import { SET_WHERE2GO, SET_PERIOD, SET_DATE, SET_RADIUS } from "../../redux/actions/where2go"
import "mapbox-gl/dist/mapbox-gl.css"
import * as s from "./style.scss"

declare const mb: { where2go: { messages: Record<string, string>, locale: string } }

const popupMessages = defineMessages({
    bestWeatherHeader: {
        defaultMessage: "Here is the best weather.",
        id: "best_weather",
    },
    bestWeatherMatch: {
        defaultMessage: "Match: {match_percent}%",
        id: "match",
    },
    bestWeatherPrecipHour: {
        defaultMessage: "Precipitation hours: {precipitation_time_percent}%",
        id: "precipitation_hours",
    },
    bestWeatherPrecipProb: {
        defaultMessage: "Precipitation probability: {precipitation_probability}%",
        id: "precipication_probability",
    },
    bestWeatherSunHour: {
        defaultMessage: "Sunshine hours: {sunshine_time_percent}%",
        id: "sunshine_hours",
    },
    changeLocationButton: {
        defaultMessage: "Best weather around here",
        id: "best_weather_around",
    },
})

interface IProps {
    mapCenter: LngLatLike
    markerPos: LngLatLike
    userLocation: ILocation
    data: IWhere2GoData | null
    isMobile: boolean
}

let marker2go: Marker
let marker2best: Marker
let marker2bestPopup: Popup
const highlightID: string[] = []
const outlineID: string[] = []
const labelStartingLayer = "poi_label"
const lang = mb.where2go.locale.substr(0, 2)

// Component for the map and everything thats on it. (Markers, popups, yellow region)
const Where2GoMap = (props: IProps) => {
    const intl = useIntl()
    const dispatch = useDispatch()
    const state = useSelector((store: IWhere2goStore) => store.where2go)
    const [mapboxgl, setMapboxGL] = React.useState(null)
    const [where2Map, setWhere2Map] = React.useState(null as Map)
    const [styleLoaded, setStyleLoaded] = React.useState(false)

    const onChange = ({ values }: { values: { period?: string, date?: string, radius?: string } }) => {
        if ("period" in values && "date" in values && "radius" in values) {
            dispatch({type: SET_WHERE2GO, payload: {
                period: parseInt(values.period, 10),
                date: parseInt(values.date, 10),
                radius: parseInt(values.radius, 10),
            }})
        } else {
            if ("period" in values) {
                dispatch({ type: SET_PERIOD, payload: parseInt(values.period, 10) })
            }
            if ("date" in values) {
                dispatch({ type: SET_DATE, payload: parseInt(values.date, 10) })
            }
            if ("radius" in values) {
                dispatch({ type: SET_RADIUS, payload: parseInt(values.radius, 10) })
            }
        }
    }

    React.useEffect(() => {
        import(/* webpackChunkName: "mapboxgl" */ "mapbox-gl").then((mapbox) => {
            setMapboxGL(mapbox)
        })
    }, [])

    React.useEffect(() => {
        if (mapboxgl) {
            const mapProps = {
                attributionControl: false,
                center: props.mapCenter,
                container: "where2goMap",
                style: `https://maptiles.meteoblue.com/styles/terrain-${lang}/style.json`,
                zoom: 7,
            }

            const where2map = new mapboxgl.Map(mapProps)
            where2map.dragRotate.disable()
            where2map.touchZoomRotate.disableRotation()
            where2map.addControl(new BestWeatherControls(intl.formatMessage, state, onChange), "top-left")
            where2map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-left")
            // Missing in mobile view
            where2map.addControl(new mapboxgl.AttributionControl(), "bottom-left")

            const centerpoint = {
                lat: props.userLocation.lat,
                lng: props.userLocation.lon,
            }

            addJSXPopup(
                mapboxgl,
                <FormattedMessage
                    defaultMessage={"You are here"}
                    description={"Popup default content"}
                    id="map_here"
                />,
                props.markerPos, 0)

            marker2go = createMarker(mapboxgl, where2map, props.markerPos, s.youAreHereIcon)
            marker2go.addTo(where2map)

            marker2bestPopup = addJSXPopup(
                mapboxgl,
                <FormattedMessage
                    defaultMessage={"Here is the best weather."}
                    description={"Best weather default message"}
                    id="best_weather"
                />,
                centerpoint, 30)

            marker2best = createMarker(mapboxgl, where2map, centerpoint, s.bestWeatherCircle, marker2bestPopup).setPopup(marker2bestPopup)
            marker2best.addTo(where2map)

            where2map.on("styledata", () => {
                const geoCoords = props.data ? invertCoords(props.data.polygones) : null

                if (!styleLoaded && geoCoords) {
                    highlightID.length = 0
                    outlineID.length = 0
                    highlightArea(where2map, geoCoords, highlightID, outlineID)
                }

                setStyleLoaded(true)
            })
            setWhere2Map(where2map)
        }
    }, [mapboxgl])

    React.useEffect(() => {
        if (props.data && styleLoaded) {
            const geoCoords = invertCoords(props.data.polygones)
            const centerpoint = { lat: props.data.centerpoint[0], lng: props.data.centerpoint[1] }

            updateMarker(where2Map, marker2go, centerpoint,
                { lng: props.userLocation.lon, lat: props.userLocation.lat },
                props.isMobile ? (props.data.zoomlevel - 1) : props.data.zoomlevel)
            updateBestWeather(marker2best, where2Map, marker2bestPopup,
                centerpoint, props.data, intl.formatMessage)
            highlightArea(where2Map, geoCoords, highlightID, outlineID)
        }
    }, [props.data, styleLoaded])

    return (
        <div id="where2goMap" className={s.where2goMap}>
            {null === mapboxgl && <Loading />}
        </div>
    )
}

const createMarker = (mapboxgl, map: Map, markerPos: LngLatLike, className: string, popup?) => {
    const el = document.createElement("div")
    el.className = className
    if (popup) { addMarkerEventListener(el, map, popup) }

    return (new mapboxgl.Marker(el).setLngLat(markerPos))
}

const updateMarker = (map: Map, marker: Marker, center: LngLatLike, coords: LngLatLike, zoom: number) => {
    if ("undefined" !== typeof map && "undefined" !== typeof marker) {
        marker.setLngLat(coords)
        map.easeTo({
            center,
            zoom,
        })
    }
}

const updateBestWeather = (bestWeather, map: Map, popup, coords: LngLatLike, additionalProps, formatMessage) => {
    if (typeof map !== "undefined") {
        popup.isOpen() ?
            popup.setLngLat(coords) :
            popup.addTo(map)

        bestWeather.setLngLat(coords)

        popup.setDOMContent(createDOMContent(
            <>
                <h3>
                    {formatMessage(popupMessages.bestWeatherHeader)}
                </h3>
                <div>
                    <span className={`${s.popupGlyph} ${s.glyph} ${s.predictability}`} />
                    {formatMessage(popupMessages.bestWeatherMatch,
                        { match_percent: additionalProps.match_percent },
                    )}
                </div>
                <div>
                    <span className={`${s.popupGlyph} ${s.glyph} ${s.precipitation}`} />
                    {formatMessage(popupMessages.bestWeatherPrecipHour,
                        { precipitation_time_percent: additionalProps.precipitation_time_percent },
                    )}
                </div>
                <div>
                    <span className={`${s.popupGlyph} ${s.glyph} ${s.precipitationProbability}`} />
                    {formatMessage(popupMessages.bestWeatherPrecipProb,
                        { precipitation_probability: additionalProps.precipitation_probability },
                    )}
                </div>
                <div>
                    <span className={`${s.popupGlyph} ${s.glyph} ${s.sunshineDuration}`} />
                    {formatMessage(popupMessages.bestWeatherSunHour,
                        { sunshine_time_percent: additionalProps.sunshine_time_percent },
                    )}
                </div>
            </>))
    }
}

const highlightArea = (map: Map, coords, highID, outID) => {
    if (highID.length !== 0 || outID.length !== 0) {
        for (const value of highID) {
            map.removeLayer(value)
            map.removeSource(value)
        }
        for (const value of outID) {
            map.removeLayer(value)
            map.removeSource(value)
        }

        highID.length = 0
        outID.length = 0
    }

    for (let i = 0; i < coords.length; i++) {
        mapAddHighlightArea(map, coords[i], i)
        highID.push(`highlight${i}`)

        mapOutlineArea(map, coords[i], i)
        outID.push(`outline${i}`)
    }
}

const mapAddHighlightArea = (map: Map, areaCoords, id: string | number) => {
    return (
        map.addLayer({
            id: `highlight${id}`,
            layout: {},
            paint: {
                "fill-color": "#F1DF3E",
                "fill-opacity": 0.3,
            },
            source: {
                data: {
                    geometry: {
                        coordinates: [areaCoords],
                        type: "Polygon",
                    },
                    type: "Feature",
                },
                type: "geojson",
            },
            type: "fill",
        }, labelStartingLayer)
    )
}

const mapOutlineArea = (map, areaCoords, id) => {
    return (
        map.addLayer({
            id: `outline${id}`,
            layout: {},
            paint: {
                "line-color": "#F1DF3E",
                "line-width": 6,
            },
            source: {
                data: {
                    geometry: {
                        coordinates: [areaCoords],
                        type: "Polygon",
                    },
                    type: "Feature",
                },
                type: "geojson",
            },
            type: "line",
        }, labelStartingLayer)
    )
}

// Inverts coordinates and adds first coordinate at the end so that the coordinates close properly
const invertCoords = (coordArray: number[][][]) => {
    const inverted: number[][][] = []

    for (let i = 0; i < coordArray.length; i++) {
        inverted[i] = [] as number[][]
        for (let j = 0; j <= coordArray[i].length; j++) {
            j === coordArray[i].length ?
                inverted[i][j] = [coordArray[i][0][1], coordArray[i][0][0]] :
                inverted[i][j] = [coordArray[i][j][1], coordArray[i][j][0]]
        }
    }

    return inverted
}

const addMarkerEventListener = (el: HTMLElement, map: Map, popup: Popup) => {
    el.addEventListener("click", (e) => {
        !popup.isOpen() ?
            popup.addTo(map) : popup.remove()

        e.stopPropagation()
    })
}

const addJSXPopup = (mapboxgl, el: JSX.Element, coords: LngLatLike, offset: number) => (
    new mapboxgl.Popup({
        anchor: "bottom",
        className: s["mapboxgl-popup"],
        closeOnClick: false,
        maxWidth: "340px",
        offset,
    })
        .setDOMContent(createDOMContent(el))
        .setLngLat(coords)
)

const createDOMContent = (content: JSX.Element) => {
    const container = document.createElement("div")
    ReactDOM.render(content, container)

    return container
}

export default React.memo(Where2GoMap)
